import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { OnboardingLayout } from './OnboardingLayout';
import { onboardingPath } from './onboardingRoutes';

interface Suggestion {
  id: string;
  targetApp: string;
  category: string;
  field: string;
  suggestedValue: Record<string, unknown>;
  userValue: Record<string, unknown> | null;
  source: 'website' | 'external' | 'inferred';
  confidence: 'high' | 'medium' | 'low';
  status: 'pending' | 'accepted' | 'rejected' | 'modified';
}

interface EnrichmentState {
  id: string;
  sourceUrl: string | null;
  status: string;
  enrichedAt: string | null;
  errorMessage: string | null;
}

interface OnboardingState {
  step: string;
  version: number;
  workspaceType: 'personal' | 'team' | 'enterprise' | null;
  enrichmentStatus: string | null;
  isComplete: boolean;
}

const APP_LABELS: Record<string, string> = {
  brandtalk: 'BrandTalk',
  'crm': 'CRM',
  outbound: 'Outbound',
  pitchless: 'PitchLess',
  vitals: 'Vitals',
};

const CONFIDENCE_COLORS: Record<string, string> = {
  high: 'bg-green-100 text-green-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-red-100 text-red-700',
};

const SOURCE_LABELS: Record<string, string> = {
  website: 'From website',
  external: 'External source',
  inferred: 'AI inferred',
};

/**
 * ReviewSuggestions page (Step 6 of onboarding)
 *
 * Displays enrichment suggestions grouped by app.
 * Each suggestion is editable and can be accepted/rejected.
 */
export function ReviewSuggestionsPage() {
  const [onboardingState, setOnboardingState] = useState<OnboardingState | null>(null);
  const [enrichment, setEnrichment] = useState<EnrichmentState | null>(null);
  const [suggestions, setSuggestions] = useState<Record<string, Suggestion[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [pollingCount, setPollingCount] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const continueUrl = searchParams.get('continue') || '';

  // Load enrichment state and suggestions
  useEffect(() => {
    async function loadData() {
      try {
        const [stateRes, enrichRes, suggestionsRes] = await Promise.all([
          fetch('/api/onboarding/state', { credentials: 'include' }),
          fetch('/api/enrichment/state', { credentials: 'include' }),
          fetch('/api/enrichment/suggestions', { credentials: 'include' }),
        ]);

        const stateData = await stateRes.json();
        const enrichData = await enrichRes.json();
        const suggestionsData = await suggestionsRes.json();

        if (stateRes.ok && stateData.state) {
          setOnboardingState(stateData.state);
        }

        if (enrichRes.ok && enrichData.enrichment) {
          setEnrichment(enrichData.enrichment);
        }

        if (suggestionsRes.ok && suggestionsData.suggestions) {
          setSuggestions(suggestionsData.suggestions);
        }
      } catch {
        setError('Failed to load suggestions');
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, [pollingCount]);

  // Enrichment is "in flight" while pending OR processing — suggestions may
  // still arrive, so the review step stays and the page keeps polling. This
  // mirrors the backend's canonical `reviewStepApplies` determination.
  const isProcessing =
    enrichment?.status === 'processing' || enrichment?.status === 'pending';

  // Poll for enrichment completion while it is still in flight.
  useEffect(() => {
    if (isProcessing) {
      const timer = setTimeout(() => setPollingCount((c) => c + 1), 3000);
      return () => clearTimeout(timer);
    }
  }, [isProcessing, pollingCount]);

  const hasSuggestions = Object.keys(suggestions).length > 0;

  // R4: the review step is conditional. Once enrichment has finished and
  // produced nothing to review, this page must never dead-end — it recovers
  // to pricing, where the conditional progression skips the empty review.
  const reviewEmpty = !isLoading && !isProcessing && !hasSuggestions;

  useEffect(() => {
    if (reviewEmpty) {
      // Review is a business-only step (R7: mode-specific routes).
      navigate(onboardingPath('business', 'pricing', continueUrl), { replace: true });
    }
  }, [reviewEmpty, continueUrl, navigate]);

  // Count stats
  const stats = useMemo(() => {
    let total = 0;
    let accepted = 0;
    let rejected = 0;
    for (const appSuggestions of Object.values(suggestions)) {
      for (const s of appSuggestions) {
        total++;
        if (s.status === 'accepted' || s.status === 'modified') accepted++;
        if (s.status === 'rejected') rejected++;
      }
    }
    return { total, accepted, rejected, pending: total - accepted - rejected };
  }, [suggestions]);

  const handleSuggestionAction = async (id: string, status: 'accepted' | 'rejected') => {
    try {
      const res = await fetch(`/api/enrichment/suggestions/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status }),
      });

      if (res.ok) {
        // Update local state
        setSuggestions((prev) => {
          const updated = { ...prev };
          for (const app of Object.keys(updated)) {
            updated[app] = updated[app].map((s) =>
              s.id === id ? { ...s, status } : s
            );
          }
          return updated;
        });
      }
    } catch {
      // Silently fail — user can retry
    }
  };

  const handleBulkAction = async (targetApp: string, status: 'accepted' | 'rejected') => {
    try {
      const res = await fetch('/api/enrichment/suggestions/bulk', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ targetApp, status }),
      });

      if (res.ok) {
        setSuggestions((prev) => {
          const updated = { ...prev };
          if (updated[targetApp]) {
            updated[targetApp] = updated[targetApp].map((s) =>
              s.status === 'pending' ? { ...s, status } : s
            );
          }
          return updated;
        });
      }
    } catch {
      // Silently fail
    }
  };

  const handleContinue = async () => {
    setIsSaving(true);
    setError('');

    try {
      const response = await fetch('/api/onboarding/step', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ step: 'review', data: {} }),
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.error || 'Failed to continue');
        return;
      }

      // Review is a business-only step. A personal-typed business
      // workspace skips invites and goes straight to complete.
      const nextStep = onboardingState?.workspaceType === 'personal' ? 'complete' : 'invites';
      navigate(onboardingPath('business', nextStep, continueUrl));
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    fetch('/api/onboarding/back', { method: 'POST', credentials: 'include' }).catch(() => {});
    navigate(onboardingPath('business', 'pricing', continueUrl));
  };

  if (isLoading) {
    return (
      <OnboardingLayout currentStep="review" workspaceType={onboardingState?.workspaceType} onboardingMode="business" wide>
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading suggestions...</p>
        </div>
      </OnboardingLayout>
    );
  }

  // R4: nothing to review — show a brief transition while the redirect
  // effect above bounces back to pricing. Never a dead-end empty screen.
  if (reviewEmpty) {
    return (
      <OnboardingLayout currentStep="review" workspaceType={onboardingState?.workspaceType} onboardingMode="business">
        <div className="bg-white shadow-lg rounded-lg p-8 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Setting up your workspace...</p>
        </div>
      </OnboardingLayout>
    );
  }

  return (
    <OnboardingLayout currentStep="review" workspaceType={onboardingState?.workspaceType} onboardingMode="business" wide>
      <div className="bg-white shadow-lg rounded-lg p-6 sm:p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-semibold text-gray-900">
            Review & Apply Suggestions
          </h1>
          <p className="text-gray-500 mt-2">
            {hasSuggestions
              ? 'Review the apps, workflows, and automations we suggest — and why — then choose what to apply'
              : 'Analyzing your website — suggestions will appear here shortly'}
          </p>
        </div>

        {/* Processing indicator */}
        {isProcessing && (
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
            <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full flex-shrink-0"></div>
            <div className="text-sm">
              <p className="font-medium text-blue-900">Analyzing your website...</p>
              <p className="text-blue-700 mt-0.5">This usually takes 15-30 seconds. Results will appear automatically.</p>
            </div>
          </div>
        )}

        {/* Stats bar */}
        {hasSuggestions && (
          <div className="flex gap-4 mb-6 text-sm">
            <span className="px-3 py-1 bg-gray-100 rounded-full text-gray-600">{stats.total} suggestions</span>
            {stats.accepted > 0 && (
              <span className="px-3 py-1 bg-green-100 rounded-full text-green-700">{stats.accepted} accepted</span>
            )}
            {stats.rejected > 0 && (
              <span className="px-3 py-1 bg-red-100 rounded-full text-red-700">{stats.rejected} rejected</span>
            )}
            {stats.pending > 0 && (
              <span className="px-3 py-1 bg-yellow-100 rounded-full text-yellow-700">{stats.pending} pending</span>
            )}
          </div>
        )}

        {/* Suggestions grouped by app */}
        {hasSuggestions && (
          <div className="space-y-6">
            {Object.entries(suggestions).map(([appId, appSuggestions]) => (
              <div key={appId} className="border border-gray-200 rounded-xl overflow-hidden">
                {/* App header */}
                <div className="bg-gray-50 px-5 py-3 flex items-center justify-between">
                  <h2 className="font-semibold text-gray-900">
                    {APP_LABELS[appId] || appId}
                    <span className="text-gray-400 font-normal ml-2">({appSuggestions.length})</span>
                  </h2>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => handleBulkAction(appId, 'accepted')}
                      className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full hover:bg-green-200 transition-colors"
                    >
                      Accept All
                    </button>
                    <button
                      type="button"
                      onClick={() => handleBulkAction(appId, 'rejected')}
                      className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-full hover:bg-red-200 transition-colors"
                    >
                      Reject All
                    </button>
                  </div>
                </div>

                {/* Suggestion items */}
                <div className="divide-y divide-gray-100">
                  {appSuggestions.map((suggestion) => {
                    const displayValue =
                      (suggestion.suggestedValue as Record<string, string>).text ||
                      (suggestion.suggestedValue as Record<string, string>).name ||
                      (suggestion.suggestedValue as Record<string, string>).description ||
                      (suggestion.suggestedValue as Record<string, string>).summary ||
                      (suggestion.suggestedValue as Record<string, string>).segment ||
                      JSON.stringify(suggestion.suggestedValue);

                    const isAccepted = suggestion.status === 'accepted' || suggestion.status === 'modified';
                    const isRejected = suggestion.status === 'rejected';

                    return (
                      <div
                        key={suggestion.id}
                        className={`px-5 py-3 flex items-start gap-4 ${
                          isRejected ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-gray-900">{suggestion.field}</span>
                            <span className={`text-xs px-1.5 py-0.5 rounded ${CONFIDENCE_COLORS[suggestion.confidence]}`}>
                              {suggestion.confidence}
                            </span>
                            <span className="text-xs text-gray-400">
                              {SOURCE_LABELS[suggestion.source]}
                            </span>
                          </div>
                          <p className="text-sm text-gray-600 truncate">{displayValue}</p>
                          <span className="text-xs text-gray-400">{suggestion.category}</span>
                        </div>

                        {/* Action buttons */}
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleSuggestionAction(suggestion.id, 'accepted')}
                            className={`p-1.5 rounded-md transition-colors ${
                              isAccepted
                                ? 'bg-green-100 text-green-600'
                                : 'text-gray-400 hover:bg-green-50 hover:text-green-600'
                            }`}
                            title="Accept"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </button>
                          <button
                            type="button"
                            onClick={() => handleSuggestionAction(suggestion.id, 'rejected')}
                            className={`p-1.5 rounded-md transition-colors ${
                              isRejected
                                ? 'bg-red-100 text-red-600'
                                : 'text-gray-400 hover:bg-red-50 hover:text-red-600'
                            }`}
                            title="Reject"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {error && (
          <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col-reverse sm:flex-row sm:justify-between gap-3">
          <button
            type="button"
            onClick={handleBack}
            className="px-6 py-2.5 text-gray-600 hover:text-gray-900 transition-colors font-medium"
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleContinue}
            disabled={isSaving || isProcessing}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? 'Continuing...' : 'Continue'}
          </button>
        </div>
      </div>
    </OnboardingLayout>
  );
}
